import { Logger } from '_common/services';

export class RichTextParser {
  private static debug: boolean = false;
  protected static version = 3;

  static get getVersion(): number {
    return RichTextParser.version;
  }

  private static transformNode(node: any): RichTextJSON {
    return {
      type: node.type,
      content: (node.children || [])
        .map((child: any) => RichTextParser.transformChild(child))
        .filter(Boolean),
    };
  }

  private static transformChild(child: any): RichTextJSON | null {
    if (!child || typeof child !== 'object') return null;

    if (child.type === 'mention') {
      return { type: 'mention', attrs: { id: child.userId } };
    }

    if (!child.text) return null;

    const marks = Object.entries(child)
      .filter(([key]) => key !== 'text' && key !== 'type')
      .map(([key, value]) => ({
        type: key,
        ...(value && typeof value === 'object' ? { attrs: value } : {}),
      }));

    return { type: 'text', text: child.text, ...(marks.length ? { marks } : {}) };
  }

  private static parse(items: any): RichTextJSON[] {
    return items.map((node: any) => RichTextParser.transformNode(node));
  }

  static parseTasks(tasks: Editor.Data.Tasks.TasksData): Editor.Data.Tasks.TasksData {
    let taskParsed: Editor.Data.Tasks.TasksData = {};
    try {
      if (RichTextParser.debug) {
        Logger.info('parsing tasks', tasks);
      }

      const keys = Object.keys(tasks);

      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const item = tasks[key];

        taskParsed[key] = RichTextParser.parseTask(item);
      }

      if (RichTextParser.debug) {
        Logger.info('parsed tasks', taskParsed);
      }
    } catch (error) {
      Logger.captureException(error);
    }

    return taskParsed;
  }

  static parseTask(task: Editor.Data.Tasks.TaskData): Editor.Data.Tasks.TaskData {
    try {
      const taskContent = task.d;

      // check content version
      if (taskContent.v !== RichTextParser.getVersion && Array.isArray(taskContent)) {
        task = {
          ...task,
          d: {
            dir: 'ltr',
            v: RichTextParser.getVersion,
            content: {
              type: 'doc',
              content: RichTextParser.parse(taskContent),
            },
          },
        };
      }

      const taskReplies = task.r;
      // replies
      if (taskReplies) {
        for (let r = 0; r < taskReplies.length; r++) {
          const replyContent = taskReplies[r].c;
          // check reply version
          if (replyContent.v !== RichTextParser.getVersion && Array.isArray(replyContent)) {
            taskReplies[r] = {
              ...taskReplies[r],
              c: {
                dir: 'ltr',
                v: RichTextParser.getVersion,
                content: {
                  type: 'doc',
                  content: RichTextParser.parse(replyContent),
                },
              },
            };
          }
        }
      }
    } catch (error) {
      Logger.captureException(error);
    }

    return task;
  }

  static parseComments(
    comments: Editor.Data.Comments.CommentsData,
  ): Editor.Data.Comments.CommentsData {
    let commentParsed: Editor.Data.Comments.CommentsData = {};
    try {
      if (RichTextParser.debug) {
        Logger.info('parsing comments', comments);
      }

      const keys = Object.keys(comments);

      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const item = comments[key];

        commentParsed[key] = RichTextParser.parseComment(item);
      }

      if (RichTextParser.debug) {
        Logger.info('parsed comments', commentParsed);
      }
    } catch (error) {
      Logger.captureException(error);
    }

    return commentParsed;
  }

  static parseComment(comment: Editor.Data.Comments.CommentData): Editor.Data.Comments.CommentData {
    try {
      if (RichTextParser.debug) {
        Logger.info('parsing comment', comment);
      }

      const content = comment.content;

      // check content version
      if (content.v !== RichTextParser.getVersion && Array.isArray(content)) {
        comment = {
          ...comment,
          content: {
            dir: 'ltr',
            v: RichTextParser.getVersion,
            content: {
              type: 'doc',
              content: RichTextParser.parse(content),
            },
          },
        };
      }

      // replies
      const numReplies = comment.comments.length;
      if (numReplies) {
        for (let i = 0; i < numReplies; i++) {
          const replyContent = comment.comments[i].content;
          // check reply version
          if (replyContent.v !== RichTextParser.getVersion && Array.isArray(replyContent)) {
            comment.comments[i] = {
              ...comment.comments[i],
              content: {
                dir: 'ltr',
                v: RichTextParser.getVersion,
                content: {
                  type: 'doc',
                  content: RichTextParser.parse(replyContent),
                },
              },
            };
          }
        }
      }

      if (RichTextParser.debug) {
        Logger.info('parsed comment', comment);
      }
    } catch (error) {
      Logger.captureException(error);
    }

    return comment;
  }

  static parseSuggestions(
    suggestions: Editor.Data.Suggestions.SuggestionsMapper,
  ): Editor.Data.Suggestions.SuggestionsMapper {
    let suggestionParsed: Editor.Data.Suggestions.SuggestionsMapper = {};
    try {
      if (RichTextParser.debug) {
        Logger.info('parsing suggestions replies', suggestions);
      }

      const keys = Object.keys(suggestions) as Array<keyof typeof suggestions>;

      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];

        suggestionParsed[key] = RichTextParser.parseSuggestion(suggestions[key]);
      }

      if (RichTextParser.debug) {
        Logger.info('parsed suggestions replies', suggestionParsed);
      }
    } catch (error) {
      Logger.captureException(error);
    }

    return suggestionParsed;
  }

  static parseSuggestion(
    suggestion: Partial<Editor.Data.Suggestions.SuggestionData>,
  ): Partial<Editor.Data.Suggestions.SuggestionData> {
    try {
      if (RichTextParser.debug) {
        Logger.info('parsing suggestion replies', suggestion);
      }
      const item = suggestion;

      if (item.comments) {
        // replies
        const numReplies = item.comments.length;

        if (numReplies) {
          for (let c = 0; c < numReplies; c++) {
            const replyContent = item.comments[c].content;

            // check reply version
            if (replyContent.v !== RichTextParser.getVersion && Array.isArray(replyContent)) {
              item.comments[c] = {
                ...item.comments[c],
                content: {
                  dir: 'ltr',
                  v: RichTextParser.getVersion,
                  content: {
                    type: 'doc',
                    content: RichTextParser.parse(replyContent),
                  },
                },
              };
            }
          }
        }
      }

      if (RichTextParser.debug) {
        Logger.info('parsed suggestion replies', suggestion);
      }
    } catch (error) {
      Logger.captureException(error);
    }

    return suggestion;
  }

  // static parseNotes(notes: Notes.NotesPayload): Notes.NotesPayload {
  //   try {
  //     const keys = Object.keys(notes.nts) as Array<keyof typeof notes.nts>;

  //     if (RichTextParser.debug) {
  //       Logger.info('parsing notes', notes.nts);
  //     }

  //     for (let i = 0; i < keys.length; i++) {
  //       const key = keys[i];
  //       const item = notes.nts[key];

  //       // check reply version
  //       if (item.content.v !== RichTextParser.getVersion && Array.isArray(item.content)) {
  //         notes.nts[key] = {
  //           ...item,
  //           content: {
  //             type: 'doc',
  //             content: RichTextParser.parse(item.content),
  //           },
  //         };
  //       }
  //     }

  //     if (RichTextParser.debug) {
  //       Logger.info('parsed notes', notes.nts);
  //     }
  //   } catch (error) {
  //     Logger.captureException(error);
  //   }

  //   return notes;
  // }
}
